import { useEffect, useState } from 'react';
import { useClient } from 'react-fetching-library';
import { listProviderTitlesAction } from '../api';
import { PROVIDER_TITLES_FALLBACK } from './constants';

/**
 * The standard provider credentials, from
 * GET /accounts/{uuid}/providers/titles.
 *
 * Memoized for the whole session rather than per component: the list is
 * the same for every account and does not change while the tab is open,
 * and the shared GET cache only lives three seconds — it exists to
 * collapse bursts of identical reads, not to hold a lookup table.
 *
 * Until the response lands, and if it never does, callers get
 * `PROVIDER_TITLES_FALLBACK`. An empty dropdown would be worse than a
 * slightly stale one: someone editing a provider's NPI should not be
 * stopped by a credential list that failed to load. The response
 * replaces it when it arrives, so a credential added on the backend
 * shows up with no dashboard release — which is why this is an endpoint
 * at all.
 */
let cachedTitles = null;
let inFlight = null;

const readTitles = payload => {
  const list = Array.isArray(payload) ? payload : payload?.items;
  if (!Array.isArray(list)) return null;
  const titles = list.filter(title => typeof title === 'string' && title.trim() !== '');
  return titles.length ? titles : null;
};

export const useProviderTitles = accountUuid => {
  const client = useClient();
  const [titles, setTitles] = useState(() => cachedTitles || PROVIDER_TITLES_FALLBACK);

  useEffect(() => {
    if (!accountUuid || cachedTitles) return undefined;
    let cancelled = false;

    // One request even when several fields mount at once — the Users
    // page can show this field in a dialog over a form that has it too.
    if (!inFlight) {
      inFlight = client
        .query(listProviderTitlesAction({ accountUuid }))
        .then(({ payload, error }) => (error ? null : readTitles(payload)))
        .catch(() => null)
        .finally(() => {
          inFlight = null;
        });
    }

    inFlight.then(fetched => {
      if (cancelled || !fetched) return;
      cachedTitles = fetched;
      setTitles(fetched);
    });

    return () => {
      cancelled = true;
    };
  }, [accountUuid, client]);

  return titles;
};

export default useProviderTitles;
